import { useFormContext } from "react-hook-form";
import { Colors } from "react-native-ui-lib";
import React from "react";
import {
  Button,
  Card,
  FlatActionList,
  Text,
  Page,
  Stack,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { StackScreenProps } from "@react-navigation/stack";
import {
  CreateStockOrderRootScreenParamList,
  StockOrderRootParamList,
} from "../../../../types";
import { StockOrderDetailsFormValues } from "../../components/StockOrderDetailsForm/types";
import { LocationIdToName } from "../../../../components";
import { textButtonProps } from "../common";
import { StockOrder } from "@towersystems/roam-common/lib/generated-types";
import { MUTATION_CREATE_STOCK_ORDER } from "../../utils";
import { useMutation } from "@apollo/client";
import { useTranslation } from "../../../../utilities/i18n";

export type CreateStockOrderScreenProps = StackScreenProps<
  CreateStockOrderRootScreenParamList & StockOrderRootParamList,
  "CreateStockOrderScreen"
>;

export const CreateStockOrderScreen = ({
  navigation,
}: CreateStockOrderScreenProps) => {
  const { watch, setError, formState, handleSubmit, trigger } =
    useFormContext<StockOrderDetailsFormValues>();

  const { t } = useTranslation("stockorder");

  const watchedLocationId = watch("locationId");

  const [m] = useMutation<{ createStockOrder: StockOrder }>(
    MUTATION_CREATE_STOCK_ORDER
  );

  const {
    errors,
    isDirty,
    isLoading,
    isSubmitting,
    isValid,
    isSubmitSuccessful,
  } = formState;

  React.useEffect(() => {
    if (errors.root?.serverError) {
      trigger();
    }
  }, [errors.root?.serverError]);

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const handleOnSubmit = React.useCallback(
    async (values: StockOrderDetailsFormValues) => {
      return await m({
        variables: {
          input: {
            locationId: values.locationId,
          },
        },
      })
        .then((result) => {
          if (!result.data || result.errors) {
            throw new Error("Failed to create stock order");
          }
          return result;
        })
        .then((result) => {
          navigation.reset({
            index: 0,
            routes: [
              { name: "StockOrderListScreen" },
              {
                name: "EditStockOrderRootScreen",
                params: {
                  stockOrderId: result.data?.createStockOrder.id,
                },
              },
            ],
          });
        })
        .catch((e) => {
          setError("root.serverError", {
            type: "server",
            message: e.message,
          });
        });
    },
    []
  );

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.create_order")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  const title = t("create_order_title");

  return (
    <Page title={title} footer={footerMarkup}>
      <Stack vertical>
        <Card title={t("card.details.title")}>
          <FlatActionList
            items={[
              {
                key: "location",
                title: t("form.location.label"),
                subTitle: t("form.location.description"),
                onPress: () => navigation.navigate("SelectLocationModalScreen"),
                componentRight: () =>
                  watchedLocationId ? (
                    <LocationIdToName
                      locationId={watchedLocationId}
                      {...(textButtonProps as any)}
                    />
                  ) : (
                    <Text {...(textButtonProps as any)}>
                      {t("form.location.placeholder")}
                    </Text>
                  ),
              },
            ]}
          />
        </Card>
      </Stack>
    </Page>
  );
};
